import axios, { InternalAxiosRequestConfig, AxiosRequestHeaders } from 'axios';
import { getActiveSkin } from '@utils/index';

// Utility function to get client validation headers
const getClientValidationHeaders = () => {
  if (typeof window === 'undefined') return {};

  const groupHash =
    import.meta.env.VITE_APP_GROUP_HASH ||
    document
      .querySelector('meta[name="x-group-hash"]')
      ?.getAttribute('content');

  let groupHost = document
    .querySelector('meta[name="x-group-host"]')
    ?.getAttribute('content');

  if (!groupHost || groupHost.trim() === '') {
    groupHost = import.meta.env.VITE_APP_STAGE;
  }

  if (!groupHost) {
    groupHost = new URL(window.location.href).host;
    if (groupHost.includes('localhost') || groupHost.includes('192.168')) {
      groupHost = import.meta.env.VITE_APP_DOMAIN_DEV || groupHost;
    }
  }

  // Custom prompt for Lolly group: pass group type so backend can use Lolly-specific prompts
  const groupType =
    document.querySelector('meta[name="x-group-type"]')?.getAttribute('content') ||
    (getActiveSkin() === 'lolly' ? 'lolly' : 'default');

  console.log('[AI API] x-group-type:', groupType);

  return {
    ...(groupHash && { 'x-group-hash': groupHash }),
    ...(groupHost && { 'x-group-host': groupHost }),
    'x-group-type': groupType,
  };
};

// Ensure baseURL ends with / and uses HTTPS
const getAIBaseURL = () => {
  const envURL = import.meta.env.VITE_AI_API_BASE_URL;
  if (envURL) {
    // Ensure URL ends with / and uses HTTPS
    let url = envURL.trim();
    if (!url.endsWith('/')) {
      url += '/';
    }
    // Force HTTPS if URL doesn't specify protocol
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }
    // Replace http:// with https:// to prevent redirects
    url = url.replace(/^http:\/\//, 'https://');
    return url;
  }
  return 'https://ai.dev.virgilcare.com/';
};

export const aiApi = axios.create({
  baseURL: getAIBaseURL(),
  headers: {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${import.meta.env.VITE_AI_KEY || ''}`,
  },
  maxRedirects: 0, // Prevent automatic redirects to avoid HTTPS -> HTTP issues
  timeout: 40000, // 40 seconds timeout for requests (reduced from default)
});

aiApi.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    // Get client validation headers
    const clientHeaders = getClientValidationHeaders();

    // Add client validation headers to all AI API requests
    config.headers = {
      ...config.headers,
      ...clientHeaders,
    } as AxiosRequestHeaders;

    return config;
  }
);

aiApi.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle redirect errors (307, 308, etc.)
    if (
      error.response &&
      (error.response.status === 307 || error.response.status === 308)
    ) {
      const redirectLocation = error.response.headers?.location;
      console.warn('AI API Redirect detected:', {
        from: error.config?.url,
        to: redirectLocation,
        status: error.response.status,
      });

      // If redirect is to HTTP, reject it
      if (redirectLocation && redirectLocation.startsWith('http://')) {
        console.error(
          'AI API Error: Server redirected to HTTP, rejecting for security'
        );
        return Promise.reject(
          new Error('Server redirected to insecure HTTP protocol')
        );
      }
    }

    // Handle AI API specific errors
    console.error('AI API Error:', error);
    return Promise.reject(error);
  }
);
